import React from 'react';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

const toNumberOrNull = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const computeElbScores = (register, scale) => {
    if (!register || !scale) return null;
    const questions = Array.isArray(scale?.questions) ? scale.questions : [];
    const responses = Array.isArray(register?.scaleResponses) ? register.scaleResponses : [];
    if (!questions.length || !responses.length) return null;

    const questionById = new Map(questions.map(q => [q.id, q]));

    let totalScore = 0;
    let totalMaxScore = 0;

    questions.forEach(q => {
        const maxQuestionScore = Math.max(
            ...(q.questionOptionScores || []).map(o => toNumberOrNull(o?.score) ?? 0),
            0
        );
        totalMaxScore += Number.isFinite(maxQuestionScore) ? maxQuestionScore : 0;
    });

    responses.forEach(resp => {
        const q = questionById.get(resp.scaleQuestionId);
        if (!q) return;
        const opt = (q.questionOptionScores || []).find(o => {
            const sid = o.scaleOptionId || o.scaleOption?.id || o.id;
            return Number(sid) === Number(resp.scaleOptionId);
        });
        const score = toNumberOrNull(opt?.score ?? resp.score);
        if (score == null) return;
        totalScore += score;
    });

    let dependencyLevel = 'Independencia total';
    if (totalScore === 0) dependencyLevel = 'Dependencia total';
    else if (totalScore <= 4) dependencyLevel = 'Dependencia severa';
    else if (totalScore <= 6) dependencyLevel = 'Dependencia moderada';
    else if (totalScore === 7) dependencyLevel = 'Dependencia ligera';

    return {
        totalScore,
        totalMaxScore,
        dependencyLevel,
    };
};

export const ScaleResultELB = ({
    title = 'Resultados',
    totalScore,
    totalMaxScore,
    dependencyLevel,
    interpretationItems = [
        { range: '8 puntos', label: 'Independencia total' },
        { range: '7 puntos', label: 'Dependencia ligera' },
        { range: '5-6 puntos', label: 'Dependencia moderada' },
        { range: '1-4 puntos', label: 'Dependencia severa' },
        { range: '0 puntos', label: 'Dependencia total' },
    ],
    onComputedResult,
    register,
    scale,
}) => {
    const derived = React.useMemo(() => computeElbScores(register, scale), [register, scale]);
    React.useEffect(() => {
        if (derived && typeof onComputedResult === 'function') {
            onComputedResult(derived);
        }
    }, [derived, onComputedResult]);

    const fallbackMax = Array.isArray(scale?.questions)
        ? scale.questions.reduce((sum, q) => sum + Math.max(...(q.questionOptionScores || []).map(o => toNumberOrNull(o?.score) ?? 0), 0), 0)
        : 8;
    const displayTotalScore = toNumberOrNull(totalScore) ?? derived?.totalScore ?? 0;
    const displayTotalMax = toNumberOrNull(totalMaxScore) ?? derived?.totalMaxScore ?? fallbackMax;
    const displayDependencyLevel = dependencyLevel || derived?.dependencyLevel;

    return (
        <Paper elevation={0} style={{ padding: 24, borderRadius: 12 }}>
            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 12, color: '#0d1b2a' }}>
                {title}
            </Typography>

            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 16, color: '#0d1b2a' }}>
                Puntuación Total: {displayTotalScore}/{displayTotalMax}
            </Typography>

            {displayDependencyLevel && (
                <List style={{ paddingTop: 0, marginBottom: 16 }}>
                    <ListItem disableGutters>
                        <ListItemIcon style={{ minWidth: 24 }}>
                            <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{ style: { color: '#0d1b2a' } }}
                            primary={`Nivel de Dependencia: ${displayDependencyLevel}`}
                        />
                    </ListItem>
                </List>
            )}

            <Box mb={1} ml={1}>
                <Typography variant="body2" style={{ fontStyle: 'italic', color: '#0d1b2a' }}>
                    Interpretación:
                </Typography>
            </Box>

            <List style={{ paddingTop: 0 }}>
                {interpretationItems.map((item, index) => (
                    <ListItem key={item?.range ?? index} disableGutters>
                        <ListItemIcon style={{ minWidth: 24 }}>
                            <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{ style: { color: '#0d1b2a' } }}
                            primary={`${item?.range || ''}: ${item?.label || ''}`}
                        />
                    </ListItem>
                ))}
            </List>
        </Paper>
    );
};

export default ScaleResultELB;
